import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminFetchProducts, adminUpdateProduct, adminUploadImage, adminCreateProduct, adminDeleteProduct, imgUrl, formatRON } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
    {children}
  </label>
);
const inputCls = "w-full border border-border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-foreground";

const blankProduct = () => {
  const rand = Math.random().toString(36).slice(2, 6);
  return {
    id: `produs-${rand}`, slug: `produs-${rand}`, code: "NEW-01", category: "tricouri",
    name_ro: "PRODUS NOU", name_en: "NEW PRODUCT", price: 100, old_price: "",
    colors: [{ name_ro: "Negru", name_en: "Black", hex: "#0a0a0a", images: [], sizes: [] }],
    sizes: [{ label: "M", stock: 10 }], images: [],
    description_ro: "", description_en: "", featured: false, presale: false, sale: false, show_size_guide: true,
    presale_note_ro: "",
    presale_note_en: "",
  };
};

const ProductsAdmin = () => {
  const { categories } = useCategories();
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [colorUploading, setColorUploading] = useState(null);

  const load = async () => { const data = await adminFetchProducts(); setProducts(data); return data; };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => { setSel(p.id); setDraft(JSON.parse(JSON.stringify(p))); };
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const createNew = async () => {
    try {
      const p = blankProduct();
      if (categories[0]) p.category = categories[0].slug;
      const created = await adminCreateProduct(p);
      await load(); openEdit(created); toast.success("Produs creat");
    }
    catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const del = async () => {
    if (!draft || !window.confirm("Ștergi acest produs?")) return;
    try { await adminDeleteProduct(draft.id); setDraft(null); setSel(null); await load(); toast.success("Șters"); }
    catch { toast.error("Eroare"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        code: draft.code, category: draft.category, name_ro: draft.name_ro, name_en: draft.name_en,
        price: parseInt(draft.price, 10) || 0,
        old_price: draft.old_price === "" || draft.old_price === null || draft.old_price === undefined ? null : (parseInt(draft.old_price, 10) || null),
        colors: draft.colors.map((c) => ({ ...c, sizes: (c.sizes || []).map((s) => ({ label: s.label, stock: parseInt(s.stock, 10) || 0 })) })),
        sizes: draft.sizes.map((s) => ({ label: s.label, stock: parseInt(s.stock, 10) || 0 })),
        images: draft.images, description_ro: draft.description_ro, description_en: draft.description_en,
        sale: !!draft.sale, show_size_guide: draft.show_size_guide !== false,
      };
      await adminUpdateProduct(draft.id, payload);
      toast.success("Salvat");
      const data = await load();
      const fresh = data.find((x) => x.id === draft.id);
      if (fresh) setDraft(JSON.parse(JSON.stringify(fresh)));
    } catch { toast.error("Eroare la salvare"); }
    setSaving(false);
  };

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const { url } = await adminUploadImage(file); set("images", [...(draft.images || []), url]); toast.success("Imagine încărcată"); }
    catch { toast.error("Upload eșuat"); }
    setUploading(false); ev.target.value = "";
  };

  const setColorImgs = (i, arr) => {
    const a = [...draft.colors];
    a[i] = { ...a[i], images: arr };
    set("colors", a);
  };

  const onUploadColor = async (i, ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setColorUploading(i);
    try {
      const { url } = await adminUploadImage(file);
      setColorImgs(i, [...(draft.colors[i].images || []), url]);
      toast.success("Imagine adăugată culorii");
    } catch { toast.error("Upload eșuat"); }
    setColorUploading(null); ev.target.value = "";
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr]">
      <aside className="border-b border-r border-border lg:h-[calc(100vh-97px)] lg:overflow-y-auto no-scrollbar">
        <button onClick={createNew} data-testid="admin-new-product" className="w-full border-b border-border bg-foreground px-4 py-3 text-left font-mono text-[11px] uppercase tracking-widest text-white hover:opacity-85">+ Produs nou</button>
        {categories.map((c) => {
          const items = products.filter((p) => p.category === c.slug);
          if (!items.length) return null;
          return (
            <div key={c.slug}>
              <div className="sticky top-0 border-b border-border bg-secondary px-4 py-2 font-mono text-[10px] uppercase tracking-widest">{c.ro} [{items.length}]</div>
              {items.map((p) => (
                <button key={p.id} onClick={() => openEdit(p)} data-testid={`admin-product-${p.slug}`}
                  className={`flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left ${sel === p.id ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden bg-white">
                    <img src={imgUrl(p.images?.[0])} alt="" className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs uc">{p.name_ro}</div>
                    <div className={`font-mono text-[10px] uppercase tracking-widest ${sel === p.id ? "text-white/70" : "text-muted-foreground"}`}>{p.code} · {formatRON(p.price)}</div>
                  </div>
                </button>
              ))}
            </div>
          );
        })}
      </aside>

      <main className="lg:h-[calc(100vh-97px)] lg:overflow-y-auto no-scrollbar">
        {!draft ? (
          <div className="flex h-full items-center justify-center p-10 font-mono text-xs uppercase tracking-widest text-muted-foreground">Selectează un produs sau creează unul nou</div>
        ) : (
          <div className="p-5 sm:p-8" data-testid="admin-editor">
            <div className="mb-6 flex items-center justify-between gap-2">
              <h2 className="text-xl uc font-semibold">{draft.name_ro}</h2>
              <div className="flex gap-2">
                <button data-testid="admin-delete" onClick={del} className="border border-destructive px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-destructive hover:bg-destructive hover:text-white">Șterge</button>
                <button data-testid="admin-save" onClick={save} disabled={saving} className="bg-foreground px-6 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:opacity-85 disabled:opacity-50">{saving ? "..." : "Salvează"}</button>
              </div>
            </div>

            <div className="mb-8 border-b border-border pb-8">
              <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Imagini principale (prima = copertă) · folosite când o culoare nu are poze proprii</div>
              <div className="flex flex-wrap gap-3">
                {(draft.images || []).map((src, i) => (
                  <div key={i} className="group relative h-28 w-28 border border-border">
                    <div className="flex h-full w-full items-center justify-center overflow-hidden bg-white p-1"><img src={imgUrl(src)} alt="" className="max-h-full max-w-full object-contain" /></div>
                    {i === 0 && <span className="absolute left-0 top-0 bg-foreground px-1 font-mono text-[8px] uppercase tracking-widest text-white">PRINCIPALĂ</span>}
                    <div className="absolute bottom-0 left-0 right-0 flex justify-between bg-white/90 px-1">
                      {i > 0 && <button onClick={() => { const a = [...draft.images]; [a[0], a[i]] = [a[i], a[0]]; set("images", a); }} className="font-mono text-[9px] uppercase hover:opacity-60">principală</button>}
                      <button data-testid={`admin-img-remove-${i}`} onClick={() => set("images", draft.images.filter((_, j) => j !== i))} className="ml-auto font-mono text-[9px] uppercase text-destructive">×</button>
                    </div>
                  </div>
                ))}
                <label className="flex h-28 w-28 cursor-pointer flex-col items-center justify-center border border-dashed border-border text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:bg-secondary">
                  {uploading ? "..." : "+ Încarcă"}
                  <input data-testid="admin-upload" type="file" accept="image/*" onChange={onUpload} className="hidden" />
                </label>
              </div>
            </div>

            <div className="mb-8 grid grid-cols-1 gap-4 border-b border-border pb-8 sm:grid-cols-2">
              <Field label="Nume (RO)"><input data-testid="edit-name-ro" className={inputCls} value={draft.name_ro} onChange={(e) => set("name_ro", e.target.value)} /></Field>
              <Field label="Nume (EN)"><input data-testid="edit-name-en" className={inputCls} value={draft.name_en} onChange={(e) => set("name_en", e.target.value)} /></Field>
              <Field label="Preț (RON) — prețul actual de vânzare"><input data-testid="edit-price" type="number" className={inputCls} value={draft.price} onChange={(e) => set("price", e.target.value)} /></Field>
              <Field label="Preț vechi (RON) — apare tăiat; lasă gol fără reducere">
                <input data-testid="edit-old-price" type="number" className={inputCls} value={draft.old_price ?? ""} onChange={(e) => set("old_price", e.target.value)} />
              </Field>
              <Field label="Cod"><input className={inputCls} value={draft.code} onChange={(e) => set("code", e.target.value)} /></Field>
              <Field label="Categorie">
                <select data-testid="edit-category" className={inputCls} value={draft.category} onChange={(e) => set("category", e.target.value)}>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.ro}</option>)}
                </select>
              </Field>
              <div className="flex items-end gap-6 pb-2">
                <label className="flex items-center gap-2"><input data-testid="edit-sale" type="checkbox" checked={!!draft.sale} onChange={(e) => set("sale", e.target.checked)} /><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">SALE!</span></label>
                <label className="flex items-center gap-2"><input data-testid="edit-size-guide" type="checkbox" checked={draft.show_size_guide !== false} onChange={(e) => set("show_size_guide", e.target.checked)} /><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Are mărimi / ghid</span></label>
              </div>
              <Field label="Descriere (RO)"><textarea className={inputCls} rows={3} value={draft.description_ro} onChange={(e) => set("description_ro", e.target.value)} /></Field>
              <Field label="Descriere (EN)"><textarea className={inputCls} rows={3} value={draft.description_en} onChange={(e) => set("description_en", e.target.value)} /></Field>
            </div>

            <div className="mb-8 border-b border-border pb-8">
              <div className="mb-3 flex items-center justify-between"><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Mărimi & Stoc</span>
                <button onClick={() => set("sizes", [...draft.sizes, { label: "NOU", stock: 0 }])} className="font-mono text-[10px] uppercase tracking-widest hover:opacity-60">+ mărime</button></div>
              <div className="space-y-2">
                {draft.sizes.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={inputCls + " max-w-[120px]"} value={s.label} onChange={(e) => { const a = [...draft.sizes]; a[i] = { ...a[i], label: e.target.value }; set("sizes", a); }} />
                    <input data-testid={`edit-stock-${s.label}`} type="number" className={inputCls + " max-w-[120px]"} value={s.stock} onChange={(e) => { const a = [...draft.sizes]; a[i] = { ...a[i], stock: e.target.value }; set("sizes", a); }} />
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">buc</span>
                    <button onClick={() => set("sizes", draft.sizes.filter((_, j) => j !== i))} className="font-mono text-xs text-destructive">×</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="pb-4">
              <div className="mb-1 flex items-center justify-between"><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Culori</span>
                <button onClick={() => set("colors", [...draft.colors, { name_ro: "Nou", name_en: "New", hex: "#000000", images: [], sizes: [] }])} className="font-mono text-[10px] uppercase tracking-widest hover:opacity-60">+ culoare</button></div>
              <p className="mb-3 font-mono text-[9px] uppercase leading-relaxed tracking-widest text-muted-foreground">Pozele adăugate la o culoare apar când clientul selectează acea culoare. Fără poze → se folosesc imaginile principale de sus.</p>
              <div className="space-y-4">
                {draft.colors.map((c, i) => (
                  <div key={i} className="border border-border p-3" data-testid={`admin-color-${i}`}>
                    <div className="flex items-center gap-2">
                      <input type="color" value={c.hex} onChange={(e) => { const a = [...draft.colors]; a[i] = { ...a[i], hex: e.target.value }; set("colors", a); }} className="h-9 w-10 flex-shrink-0 border border-border bg-white p-0" />
                      <input className={inputCls} placeholder="RO" value={c.name_ro} onChange={(e) => { const a = [...draft.colors]; a[i] = { ...a[i], name_ro: e.target.value }; set("colors", a); }} />
                      <input className={inputCls} placeholder="EN" value={c.name_en} onChange={(e) => { const a = [...draft.colors]; a[i] = { ...a[i], name_en: e.target.value }; set("colors", a); }} />
                      <button data-testid={`admin-color-remove-${i}`} onClick={() => set("colors", draft.colors.filter((_, j) => j !== i))} className="flex-shrink-0 font-mono text-xs text-destructive">×</button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(c.images || []).map((src, k) => (
                        <div key={k} className="group relative h-20 w-20 border border-border">
                          <div className="flex h-full w-full items-center justify-center overflow-hidden bg-white p-1"><img src={imgUrl(src)} alt="" className="max-h-full max-w-full object-contain" /></div>
                          {k === 0 && <span className="absolute left-0 top-0 bg-foreground px-1 font-mono text-[7px] uppercase tracking-widest text-white">1</span>}
                          <div className="absolute bottom-0 left-0 right-0 flex justify-between bg-white/90 px-0.5">
                            {k > 0 && <button onClick={() => { const arr = [...c.images]; [arr[0], arr[k]] = [arr[k], arr[0]]; setColorImgs(i, arr); }} className="font-mono text-[8px] uppercase hover:opacity-60">1ª</button>}
                            <button data-testid={`admin-color-${i}-img-remove-${k}`} onClick={() => setColorImgs(i, c.images.filter((_, j) => j !== k))} className="ml-auto font-mono text-[9px] uppercase text-destructive">×</button>
                          </div>
                        </div>
                      ))}
                      <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center border border-dashed border-border text-center font-mono text-[9px] uppercase tracking-widest text-muted-foreground hover:bg-secondary">
                        {colorUploading === i ? "..." : "+ poză"}
                        <input data-testid={`admin-color-upload-${i}`} type="file" accept="image/*" onChange={(e) => onUploadColor(i, e)} className="hidden" />
                      </label>
                    </div>
                    <div className="mt-3 border-t border-border pt-3">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" data-testid={`admin-color-${i}-sizes-toggle`}
                          checked={Array.isArray(c.sizes) && c.sizes.length > 0}
                          onChange={(e) => {
                            const a = [...draft.colors];
                            a[i] = { ...a[i], sizes: e.target.checked ? draft.sizes.map((s) => ({ label: s.label, stock: parseInt(s.stock, 10) || 0 })) : [] };
                            set("colors", a);
                          }} />
                        <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Mărimi & stoc separate pe culoarea asta</span>
                      </label>
                      {Array.isArray(c.sizes) && c.sizes.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {c.sizes.map((s, si) => (
                            <div key={si} className="flex items-center gap-2">
                              <input className={inputCls + " max-w-[100px]"} value={s.label}
                                onChange={(e) => { const a = [...draft.colors]; const sz = [...a[i].sizes]; sz[si] = { ...sz[si], label: e.target.value }; a[i] = { ...a[i], sizes: sz }; set("colors", a); }} />
                              <input type="number" data-testid={`admin-color-${i}-stock-${si}`} className={inputCls + " max-w-[100px]"} value={s.stock}
                                onChange={(e) => { const a = [...draft.colors]; const sz = [...a[i].sizes]; sz[si] = { ...sz[si], stock: e.target.value }; a[i] = { ...a[i], sizes: sz }; set("colors", a); }} />
                              <span className="font-mono text-[9px] uppercase text-muted-foreground">buc</span>
                              <button onClick={() => { const a = [...draft.colors]; a[i] = { ...a[i], sizes: a[i].sizes.filter((_, j) => j !== si) }; set("colors", a); }} className="font-mono text-xs text-destructive">×</button>
                            </div>
                          ))}
                          <button onClick={() => { const a = [...draft.colors]; a[i] = { ...a[i], sizes: [...a[i].sizes, { label: "NOU", stock: 0 }] }; set("colors", a); }} data-testid={`admin-color-${i}-size-add`} className="font-mono text-[9px] uppercase tracking-widest hover:opacity-60">+ mărime</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProductsAdmin;
